import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A possibly-multi-valued server field as a list, whatever shape arrived.
 *
 * `study_status.disease` became a JSONB array so a study can declare several
 * anomalies, and the gateway coerces on the way out — so in steady state this is a
 * no-op. It exists for the window where it is not: `api-gateway` and `tagging-ui`
 * are separate images updated at separate moments, so a rolling deploy briefly pairs
 * a new UI with a gateway still returning a bare string. Without this, `.join()` on
 * that string throws inside render and the studies table white-screens — for a field
 * that is decoration.
 *
 * Also what lets a fixture keep writing `disease: 'nodule'` without every test file
 * having to know the wire shape.
 */
export function asList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string')
  if (typeof value === 'string') return value.trim() ? [value] : []
  return []
}

/**
 * A study UID short enough to sit in a table cell, truncated from the **END**.
 *
 * The leading digits are the same institutional prefix on every study, so a leading
 * truncation identifies nothing. Always pair with `title={uid}` so the full value is
 * one hover away.
 */
export function shortUid(uid: string): string {
  return uid.length > 14 ? `…${uid.slice(-14)}` : uid
}
